import {isRejectedWithValue, isFulfilled, Middleware} from '@reduxjs/toolkit';
import {transformError} from '../../utils/errorTransform';

/**
 * Redux middleware that intercepts all RTK Query rejected and fulfilled actions
 * to handle error transformation and dispatch to ErrorContext.
 *
 * Replaces custom useApiQuery/useApiMutation/useApiLazyQuery hooks that wrapped
 * RTK Query hooks with identical error handling logic.
 *
 * @example
 * ```typescript
 * import { errorMiddleware } from './middleware/errorMiddleware';
 *
 * export const store = configureStore({
 *   middleware: (getDefaultMiddleware) =>
 *     getDefaultMiddleware()
 *       .concat(emptyApi.middleware)
 *       .concat(errorMiddleware),  // Add AFTER RTK Query middleware
 * });
 * ```
 */
export const errorMiddleware: Middleware = (api) => (next) => (action) => {
    // Handle rejected actions (failed queries/mutations)
    if (isRejectedWithValue(action)) {
        const error = action.payload;
        const endpointName = action.meta?.arg?.endpointName || 'unknown';

        // Transform error to user-friendly format
        const transformedError = transformError(error, endpointName);

        // Dispatch to ErrorContext for UI display
        // Note: ErrorContext actions (setError, recordNetworkFailure) will be
        // dispatched when ErrorContext is integrated with Redux store
        console.error('RTK Query request failed:', {
            endpoint: endpointName,
            error: transformedError,
        });

        // Track network failures for offline detection
        if (transformedError.category === 'NETWORK') {
            console.warn(
                'Network failure detected, consecutive failures tracking needed'
            );
        }
    }

    // Handle fulfilled actions (successful queries/mutations)
    if (isFulfilled(action)) {
        // Clear error state on success
        // Note: ErrorContext clearError will be dispatched when integrated
        const endpointName = action.meta?.arg?.endpointName;
        if (endpointName) {
            console.log('RTK Query request succeeded:', endpointName);
        }
    }

    return next(action);
};
